import type { AbsoluteTimeRange, TimeRangeInputValue } from '@chronosphereio/core';
import { createContext, type ReactNode, useCallback, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import type { TimeRangeProviderOptions } from './time-range-api';

const TimeRangeProviderRegistryContext = createContext<ReturnType<typeof useTimeRangeRegistryImpl> | undefined>(
  undefined
);

// Provider implementation which basically just keeps track of API refs for `TimeRangeProvider` instances.
function useTimeRangeRegistryImpl() {
  const apiByUrlParamsScope = useRef<Map<string, TimeRangeInUrlApi>>(new Map());
  return useMemo(() => ({ apiByUrlParamsScope }), [apiByUrlParamsScope]);
}

/**
 * A top-level provider (meant to live near the top of an application) that allows access to an imperative API for
 * getting the current time range in the URL, even if the consumer component is not a descendant of the
 * `TimeRangeProvider` that's modifying the URL. See `useTimeRangeInUrl` for that API.
 */
export function TimeRangeProviderRegistry({ children }: { children: ReactNode }) {
  const ctx = useTimeRangeRegistryImpl();
  return <TimeRangeProviderRegistryContext.Provider value={ctx}>{children}</TimeRangeProviderRegistryContext.Provider>;
}

/**
 * The imperative API exposed by `useTimeRangeInUrl`.
 */
export interface TimeRangeInUrlApi {
  /**
   * Gets the current `TimeRangeInputValue` in the URL. Returns `undefined` if the provider is missing or there is no
   * time range in the URL.
   */
  getTimeRange(): TimeRangeInputValue | undefined;

  /**
   * Gets the `AbsoluteTimeRange` version of the time range in the URL. Returns `undefined` if the provider is missing
   * or there is no time range in the URL.
   */
  getAbsoluteTimeRange(): AbsoluteTimeRange | undefined;
}

/**
 * Provides an imperative API for getting the current time range / absolute time range that's represented in the URL
 * without the consumer component needing to be under the `TimeRangeProvider` that's modifying the URL.
 *
 * NOTE: This should be used only as a last resort and is mainly here to support the `HeaderCopyUrlButton` in `cloud`.
 */
export function useTimeRangeInUrl(urlParamsScope: string = ''): TimeRangeInUrlApi {
  const registryContext = useContext(TimeRangeProviderRegistryContext);

  const getTimeRange = useCallback(
    () => registryContext?.apiByUrlParamsScope.current.get(urlParamsScope)?.getTimeRange(),
    [registryContext?.apiByUrlParamsScope, urlParamsScope]
  );
  const getAbsoluteTimeRange = useCallback(
    () => registryContext?.apiByUrlParamsScope.current.get(urlParamsScope)?.getAbsoluteTimeRange(),
    [registryContext?.apiByUrlParamsScope, urlParamsScope]
  );

  return { getTimeRange, getAbsoluteTimeRange };
}

/**
 * For use in the `TimeRangeProvider`, this registers that provider with the `TimeRangeProviderRegistry`.
 */
export function useRegisterTimeRangeProvider(
  timeRange: TimeRangeInputValue,
  absoluteTimeRange: AbsoluteTimeRange,
  options: TimeRangeProviderOptions
) {
  const { enableUrlParams, urlParamsScope } = options;
  const registryContext = useContext(TimeRangeProviderRegistryContext);

  // The ref passed to `useImperativeHandle` that registers the imperative API with the provider's context
  const callbackRef = useCallback(
    (api: TimeRangeInUrlApi | null) => {
      // If the provider (i.e. registry context) is missing do nothing since this is really meant for one very
      // specific use case in `cloud` and so other consumers (e.g. pipeline) probably don't care
      if (registryContext === undefined) {
        return;
      }

      // We only care about providers with URL params enabled
      if (enableUrlParams === false) {
        return;
      }

      // React will send null when the component unmounts or the API is about the change so stop tracking
      if (api === null) {
        registryContext.apiByUrlParamsScope.current.delete(urlParamsScope);
        return;
      }

      // Otherwise register normally
      registryContext.apiByUrlParamsScope.current.set(urlParamsScope, api);
    },
    [enableUrlParams, registryContext, urlParamsScope]
  );

  // Register the API
  useImperativeHandle(
    callbackRef,
    () => ({
      getTimeRange() {
        return timeRange;
      },
      getAbsoluteTimeRange() {
        return absoluteTimeRange;
      },
    }),
    [timeRange, absoluteTimeRange]
  );
}
